// Shared plain-language formatting for the client cabinet.
//
// Ukrainian is the source language; `tr()` and `currentLang()` make the month
// names, plurals and number grouping follow the client's cabinet language
// (backlog 87c81e60 / 6f47ee8f) without every call site passing a locale.
import { tr, currentLang } from "./i18n";
import { convertEntries } from "./money";

const intlLocale = () => (currentLang() === "en" ? "en-GB" : "uk-UA");

const MONTHS_UK = [
  "січень", "лютий", "березень", "квітень", "травень", "червень",
  "липень", "серпень", "вересень", "жовтень", "листопад", "грудень",
];

// Dates arrive in two formats in this DB: ISO "2026-07-15(T…)" and the older
// "15.07.2026" — parse both, return null (never "Invalid Date") otherwise.
export const parseDay = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = str.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  return null;
};

export const fmtDate = (s) => {
  const d = parseDay(s);
  if (!d) return null;
  return d.toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit", year: "numeric" });
};

export const fmtShortDate = (s) => {
  const d = parseDay(s);
  if (!d) return null;
  return d.toLocaleDateString(intlLocale(), { day: "numeric", month: "long" });
};

// "2026-08" → "серпень 2026"
export const fmtMonth = (ym) => {
  if (!ym || !/^\d{4}-\d{2}/.test(ym)) return null;
  const [y, m] = ym.split("-");
  return `${tr(MONTHS_UK[Number(m) - 1])} ${y}`;
};

export const daysBetween = (from, to) =>
  Math.round((to.getTime() - from.getTime()) / 86400000);

export const today = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// plural(5, ["день", "дні", "днів"]) — Ukrainian forms in, the active
// language out. English has two forms, so it takes the singular or the
// genitive-plural slot and the dictionary carries the pair.
export const plural = (n, forms) => {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (currentLang() === "en") return tr(Math.abs(n) === 1 ? forms[0] : forms[2]);
  if (a > 10 && a < 20) return tr(forms[2]);
  if (b > 1 && b < 5) return tr(forms[1]);
  if (b === 1) return tr(forms[0]);
  return tr(forms[2]);
};

export const days = (n) => `${n} ${plural(n, ["день", "дні", "днів"])}`;

export const money = (amount, currency) =>
  `${Number(amount || 0).toLocaleString(intlLocale(), { maximumFractionDigits: 2 })} ${currency || "USD"}`;

// Never add different currencies into one number — group per currency.
export const sumByCurrency = (rows, pick = (r) => r.amount) => {
  const out = {};
  rows.forEach((r) => {
    const c = r.currency || "USD";
    out[c] = (out[c] || 0) + (pick(r) || 0);
  });
  return Object.entries(out).filter(([, v]) => v > 0);
};

export const moneyList = (entries) => {
  // One converted figure when the client chose a display currency, the exact
  // per-currency list otherwise. See money.js. backlog ef0c7e7f.
  const one = convertEntries(entries);
  if (one) return `${one.approximate ? "≈ " : ""}${money(one.amount, one.currency)}`;
  return entries.map(([c, v]) => money(v, c)).join(" + ");
};

export const PROJECT_STATUS_UK = {
  proposal_sent: "Готуємо пропозицію",
  contract_negotiation: "Погоджуємо договір",
  awaiting_payment: "Чекаємо передоплату",
  sent_to_pool: "Запускаємо роботу",
  in_progress: "В роботі",
  awaiting_client: "Чекаємо на вас",
  completed: "Завершено",
};

export const isProjectDone = (p) => p?.status === "completed";

export const initials = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "—";
